// Shared presentation for the dedicated /pricing route and its pricing subcomponents. Fixed
// self-serve values come only from the
// reconciled billing catalogue; the separate per-student configuration is never substituted for a
// missing fixed price. Both surfaces therefore disclose the same verified/unavailable state.
import React from "react";
import { Link } from "react-router-dom";
import { Star, Check, ArrowRight, Zap } from "lucide-react";
import { cn, inrFull } from "../../lib/utils";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
import { StaggerItem, PRICING_ICONS, PLAN_TAGLINES, PLAN_ICONS, PRICE_BTN_BASE, PRICE_BTN } from "./parts1";
import { CTA_PRIMARY_LABEL } from "../../components/marketing/navigation";

export function pricingTaxDisplay(pricingIntegrity) {
  const value = pricingIntegrity?.tax_display;
  return typeof value === "string" && value.trim()
    ? value.trim()
    : "Tax treatment unavailable";
}

// Fixed-plan cards from the served pricing config — same shape as the CMS `pricing` cards.
// Static copy mirrors the tier narrative; the annual note rides `annual`, not the feature list.
export function buildFixedCards(priceCfg, pricingIntegrity = null) {
  // Retain the shared signature while fixed cards intentionally derive only from the reconciled
  // billing catalogue. The proposal model must never be substituted for self-serve prices.
  void priceCfg;
  const taxDisplay = pricingTaxDisplay(pricingIntegrity);
  const billingRows = Object.fromEntries((pricingIntegrity?.rows || []).map((row) => [row.key, row]));
  const commercial = (key) => {
    const row = billingRows[key];
    const monthly = Number.isInteger(row?.billing_plan_paise) ? row.billing_plan_paise : null;
    const annual = Number.isInteger(row?.billing_annual_paise) ? row.billing_annual_paise : null;
    return {
      monthly,
      annual,
      verified: monthly !== null && annual !== null,
      price: monthly !== null ? inrFull(monthly) : "Verify with us",
      period: monthly !== null ? "/mo base" : "",
      annualLine: annual !== null
        ? `${inrFull(annual)}/year base · ${taxDisplay}`
        : "Commercial schedule is not shown until the billing catalogue is verified.",
    };
  };
  const starter = commercial("starter");
  const growth = commercial("growth");
  const premium = commercial("premium");
  // WP-12: every bullet names a capability, a number or an audience — never a pointer at
  // "the live catalogue" / "the plan row" / "the written schedule". Capability claims are
  // grounded in the real entitlement tiers (core/entitlements.py: Growth adds the campus +
  // accounting depth, Premium adds assistant/automation/insights/LMS, Enterprise adds
  // group console + white-label + biometric). Do not invent student limits here — none
  // exist in the entitlement engine.
  return [
    { id: "f_starter", planId: "plan_starter", name: "Starter", price: starter.price, period: starter.period, icon: "zap",
      popular: false, cta: CTA_PRIMARY_LABEL, annual: starter.annualLine, commercialVerified: starter.verified,
      audience: "For a single school running the day-to-day core",
      features: [
        "Admissions, attendance, fees in ₹, exams & report cards",
        "Certificates, the full reports library & a reviewed data-migration preview",
        "Priced per school — no per-staff seat pricing",
      ] },
    { id: "f_growth", planId: "plan_growth", name: "Growth", price: growth.price, period: growth.period, icon: "crown",
      popular: true, cta: CTA_PRIMARY_LABEL, annual: growth.annualLine, commercialVerified: growth.verified,
      audience: "For schools running fees, finance and the campus end to end",
      features: [
        "Everything in Starter, plus accounting with Tally export & online exams",
        "Library, hostel, health room, front office, inventory & bus tracking",
        "Online payments settle to your school's own gateway account",
      ] },
    { id: "f_premium", planId: "plan_premium", name: "Premium", price: premium.price, period: premium.period, icon: "crown",
      popular: false, cta: CTA_PRIMARY_LABEL, annual: premium.annualLine, commercialVerified: premium.verified,
      audience: "For schools that want the AI and automation layer",
      features: [
        "Everything in Growth, plus the Aira AI assistant & rule-based automation",
        "Supervised fee recovery, AI insights & the digital classroom (LMS)",
        "Aira proposes — a person approves before any record changes",
      ] },
    { id: "f_enterprise", planId: "plan_enterprise", name: "Enterprise", price: "Custom", period: "", icon: "shield",
      popular: false, cta: "Contact sales",
      commercialVerified: true, commercialState: "proposal",
      audience: "For trusts and chains running more than one school",
      features: [
        "Multi-campus group console with consolidated billing",
        "White-label branding & biometric device support",
        "Implementation and support agreed in writing",
      ] },
  ];
}

// The comparison matrix reuses the SAME card price derivation so matrix and cards cannot disagree.
// Claim integrity: the trial column must read as the free trial it is — the server row carries the
// post-trial LIST price, which rendered as "Free Trial — ₹1,000/mo" (a bait-and-switch look).
export function mapMatrixPlans(plans, fixedCards) {
  const byId = Object.fromEntries(fixedCards.map((card) => [card.planId, card]));
  return (plans || []).map((pl) => {
    // WP-03: the trial column IS the full product for a limited time (plan_trial resolves to
    // the complete enableable feature set by design) — the header must say so, or the matrix
    // reads as "the free trial beats the paid plans".
    if (pl.id === "plan_trial") {
      return { ...pl, name: "Free trial", price: "₹0", period: " · 90 days", trialFullProduct: true };
    }
    const f = byId[pl.id];
    return f ? { ...pl, price: f.price, period: f.period } : pl;
  });
}

// One premium fixed-plan card (blue-featured "Most popular", warm-orange badge, per-plan CTA style).
// `pl` is {id,name,price,period,icon,popular,annual?,features[],cta}; mount inside a <Stagger> grid.
export function PlanCard({ pl, navigate, reduceMotion, onContactSales }) {
  const { paused } = useMarketingMotion();
  const motionOff = reduceMotion || paused;
  const pop = pl.popular;
  const Icon = PLAN_ICONS[pl.name] || PRICING_ICONS[pl.icon] || Zap;
  const isEnterprise = pl.name === "Enterprise";
  const btnKind = pop ? "primary" : isEnterprise ? "orangeOutline" : "blueOutline";
  return (
    <StaggerItem as="div" v="up"
      whileHover={motionOff ? undefined : { y: -6 }}
      className={cn(
        "group/card relative flex flex-col rounded-[28px] bg-surface p-5 sm:p-6 lg:p-7 transition-[box-shadow,border-color] duration-300",
        pop
          ? "border-[1.5px] border-[#2563EB] shadow-[0_24px_60px_rgba(37,99,235,0.14)] hover:shadow-[0_32px_72px_rgba(37,99,235,0.24)]"
          : cn(
              "border border-[rgba(15,23,42,0.08)] shadow-[0_20px_50px_rgba(15,23,42,0.08)] hover:shadow-[0_26px_60px_rgba(15,23,42,0.12)]",
              isEnterprise ? "hover:border-orange-border" : "hover:border-primary/50"
            )
      )}
      data-testid={`pricing-${pl.name}`}
      data-commercial-state={pl.commercialState || (pl.commercialVerified ? "verified" : "unavailable")}>
      {/* Featured aura is intentionally settled: hierarchy without ambient looping. */}
      {pop && (
        <span className="absolute -inset-px rounded-[28px] pointer-events-none shadow-[0_0_26px_1px_rgba(37,99,235,0.16)]" aria-hidden="true" />
      )}
      {/* "Most popular" badge — attached to the top edge, warm-orange gradient + soft glow. */}
      {pop && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
          <span className="inline-flex items-center gap-1 rounded-full bg-[linear-gradient(135deg,#F97316,#FB923C)] text-white text-[11px] font-bold tracking-wide px-3.5 py-1.5 shadow-[0_8px_22px_rgba(249,115,22,0.35)] whitespace-nowrap">
            <Star className="w-3 h-3 fill-white" aria-hidden="true" /> Most popular
          </span>
        </div>
      )}
      {/* icon — soft blue-tint rounded square, blue glyph; floats on hover */}
      <span className="w-14 h-14 rounded-2xl bg-primary/10 ring-1 ring-primary/15 grid place-items-center mb-[18px] transition-transform duration-300 group-hover/card:-translate-y-0.5 group-hover/card:scale-105">
        <Icon className="w-7 h-7 text-primary" strokeWidth={1.75} aria-hidden="true" />
      </span>
      {/* name + audience line (WP-12: who the plan is for beats a generic tagline) */}
      <h3 className="font-heading font-bold text-ink text-[24px] leading-tight tracking-tight">{pl.name}</h3>
      {(pl.audience || PLAN_TAGLINES[pl.name]) && (
        <p className="text-[15px] text-ink-2 mt-1.5 leading-snug">{pl.audience || PLAN_TAGLINES[pl.name]}</p>
      )}
      {/* price — nowrap so a compound price string ("₹20–40", "+₹7,999") never
          breaks mid-number; the period label wraps beside it instead */}
      <div className="mt-5 flex items-baseline gap-1 flex-wrap">
        <span className="font-heading font-extrabold text-[38px] text-ink tracking-tight leading-none whitespace-nowrap">{pl.price}</span>
        {pl.period && <span className="text-[15px] font-medium text-ink-subtle">{pl.period}</span>}
      </div>
      {pl.annual && <p className="mt-1.5 text-[13px] text-ink-subtle leading-snug">{pl.annual}</p>}
      {/* divider */}
      <div className="mt-[18px] border-t border-line" />
      {/* features — soft blue check circles (never green) */}
      <ul className="mt-[18px] space-y-3 text-[14.5px] text-ink-2 flex-1">
        {(pl.features || []).map((x) => (
          <li key={x} className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-primary/10 grid place-items-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-primary" strokeWidth={3} aria-hidden="true" /></span>
            <span className="leading-snug">{x}</span>
          </li>
        ))}
      </ul>
      {/* CTA — per-plan style; arrow nudges on hover. WP-05: the trial CTA navigates, so it
          is a real Link; Enterprise opens the contact-sales modal, so it stays a button. */}
      {isEnterprise ? (
        <button type="button" onClick={() => {
          if (onContactSales) onContactSales();
          else navigate("/contact");
        }} className={cn(PRICE_BTN_BASE, PRICE_BTN[btnKind], "mt-[22px]")}>
          {pl.cta || "Contact sales"} <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" aria-hidden="true" />
        </button>
      ) : (
        <Link to="/register" className={cn(PRICE_BTN_BASE, PRICE_BTN[btnKind], "mt-[22px]")}>
          {pl.cta || CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" aria-hidden="true" />
        </Link>
      )}
    </StaggerItem>
  );
}

// Landing-only presentation: the three self-serve plans read as one commercial ladder instead
// of four detached cards. The dedicated /pricing route keeps PlanCard above because it needs the
// conventional card treatment beside the full comparison matrix.
export function LandingPlanColumn({ pl, order = 1 }) {
  const Icon = PLAN_ICONS[pl.name] || PRICING_ICONS[pl.icon] || Zap;
  const featured = Boolean(pl.popular);
  return (
    <article
      className={cn(
        "relative flex min-w-0 flex-col px-5 py-7 sm:px-7 sm:py-8 lg:min-h-[34rem]",
        "border-t border-line first:border-t-0 lg:border-l lg:border-t-0 lg:first:border-l-0",
        featured && "bg-primary-50/70 dark:bg-primary/10",
      )}
      data-testid={`pricing-${pl.name}`}
      data-commercial-state={pl.commercialState || (pl.commercialVerified ? "verified" : "unavailable")}
    >
      {featured && <span className="absolute inset-x-0 top-0 h-1 bg-primary" aria-hidden="true" />}

      <div className="flex items-center justify-between gap-4">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/15 bg-surface text-primary shadow-card">
          <Icon className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />
        </span>
        <span className="font-heading text-[11px] font-bold uppercase tracking-[0.18em] text-ink-subtle">
          {String(order).padStart(2, "0")}
        </span>
      </div>

      <div className="mt-7 flex min-h-[6.75rem] flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-heading text-[25px] font-bold tracking-[-0.03em] text-ink">{pl.name}</h3>
          {featured && (
            <span className="rounded-full border border-primary/20 bg-surface px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.13em] text-accent-active">
              Most popular
            </span>
          )}
        </div>
        <p className="mt-2 max-w-[18rem] text-[14px] leading-6 text-ink-2">
          {pl.audience || PLAN_TAGLINES[pl.name]}
        </p>
      </div>

      <div className="mt-5 border-y border-line py-5">
        <div className="flex flex-wrap items-end gap-x-1.5 gap-y-1">
          <span className="font-heading text-[36px] font-extrabold leading-none tracking-[-0.04em] text-ink whitespace-nowrap">
            {pl.price}
          </span>
          {pl.period && <span className="pb-0.5 text-[13px] font-semibold text-ink-subtle">{pl.period}</span>}
        </div>
        {pl.annual && <p className="mt-2 text-[12.5px] leading-5 text-ink-subtle">{pl.annual}</p>}
      </div>

      <ul className="mt-6 flex-1 space-y-4 text-[14px] text-ink-2">
        {(pl.features || []).map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
              <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
            </span>
            <span className="leading-6">{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/register"
        className={cn(
          "mt-7 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full px-5 text-[14px] font-semibold",
          "transition-[background-color,border-color,color,transform] duration-fast ease-premium active:scale-[0.98]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2",
          featured
            ? "bg-primary text-primary-fg hover:bg-primary-700"
            : "border border-line-strong bg-surface text-ink hover:border-primary/45 hover:text-accent-active",
        )}
      >
        {pl.cta || CTA_PRIMARY_LABEL}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

export function LandingEnterpriseLane({ pl, navigate, onContactSales }) {
  const Icon = PLAN_ICONS[pl.name] || PRICING_ICONS[pl.icon] || Zap;
  return (
    <article
      className="relative overflow-hidden border-t border-white/15 bg-vidya-950 px-5 py-7 text-white sm:px-7 sm:py-8 lg:grid lg:grid-cols-[0.8fr_1.2fr_auto] lg:items-center lg:gap-9"
      data-testid={`pricing-${pl.name}`}
      data-commercial-state={pl.commercialState || "proposal"}
    >
      <div className="pointer-events-none absolute inset-y-0 right-0 w-2/5 bg-[radial-gradient(circle_at_80%_50%,rgba(37,99,235,0.23),transparent_66%)]" aria-hidden="true" />
      <div className="relative flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/15 bg-white/10 text-white">
          <Icon className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />
        </span>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/65">Multi-school procurement</p>
          <h3 className="mt-1 font-heading text-[26px] font-bold tracking-[-0.03em] text-white">{pl.name}</h3>
          <p className="mt-2 max-w-sm text-[13.5px] leading-6 text-white/70">
            {pl.audience || PLAN_TAGLINES[pl.name]}
          </p>
        </div>
      </div>

      <ul className="relative mt-6 grid gap-3 text-[13.5px] text-white/75 sm:grid-cols-2 lg:mt-0">
        {(pl.features || []).map((feature) => (
          <li key={feature} className="flex items-start gap-2.5">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#BAE6FD]" strokeWidth={2.5} aria-hidden="true" />
            <span className="leading-5">{feature}</span>
          </li>
        ))}
      </ul>

      <div className="relative mt-7 flex flex-col items-start gap-3 lg:mt-0 lg:items-end">
        <span className="font-heading text-[28px] font-bold tracking-[-0.03em] text-white">{pl.price}</span>
        <button
          type="button"
          onClick={() => {
            if (onContactSales) onContactSales();
            else navigate("/contact");
          }}
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full !bg-white px-5 text-[14px] font-bold !text-vidya-950 transition-transform duration-fast ease-premium active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-vidya-950 lg:w-auto"
        >
          {pl.cta || "Contact sales"}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}
